import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonContent,
} from "@ionic/react";


// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion

// Question Type Definitions
interface BaseQuestion {
    question: string;
    explanation: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    answer: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[];
    answer: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    answer: string;
}




const EarthIntroduction: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showExplanation, setShowExplanation] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    const loadRandomSyllabusQuestions = async () => {
        setShowExplanation(false);
        try {
            let data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + "ClassThreeSocialStudies/EarthIntroduction.json"
                );
                data = await response.json();
            } else {
                const module = await import(
                    `../../../datas/ClassThreeSocialStudies/EarthIntroduction.json`
                );
                data = module.default as Question[];
            }

            setQuestions(data);
            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
            setCurrentQuestion(shuffledQuestions[currentIndex]);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;
        if (!currentQuestion) return;

        let correctAnswer: string | boolean = "";
        if (
            currentQuestion.type === "multiple-choice" ||
            currentQuestion.type === "fill-in-the-blank"
        ) {
            correctAnswer = currentQuestion.answer;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (currentQuestion.type === "true-false") {
            correctAnswer = currentQuestion.answer;
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setShowExplanation(false);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                    setCurrentQuestion(questions[nextIndex]);
                }
                else {
                    setCurrentIndex(0);
                    loadRandomSyllabusQuestions();
                }
            }, 5000);
        }
        else {
            setShowExplanation(true);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        loadRandomSyllabusQuestions();

        document.title =
            "The Earth – An Introduction | Class 3 Social Studies (CBSE & ICSE)";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Discover the shape, features, and importance of Earth in this Class 3 Social Studies chapter. Learn about continents, oceans, and our planet’s unique characteristics for CBSE & ICSE students." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <header>
                                <h1>Chapter 6: The Earth: An Introduction</h1>
                            </header>

                            <section>
                                <h2>Introduction</h2>
                                <p>The Earth is our home. It is one of the eight planets in the Solar System and the only one known to have life. From mountains to oceans, forests to deserts, the Earth is full of amazing landforms and natural wonders.</p>
                            </section>

                            <section>
                                <h2>Shape of the Earth</h2>
                                <ul>
                                    <li>Long ago, people thought the Earth was flat.</li>
                                    <li>Now we know it is round, like a ball, but slightly flattened at the top and bottom. This shape is called a <b>sphere</b> or <b>geoid</b>.</li>
                                    <li>We cannot feel the Earth moving, but it is always spinning and moving around the Sun.</li>
                                </ul>
                            </section>

                            <section>
                                <h2>Movements of the Earth</h2>
                                <p>The Earth has two main types of movements:</p>
                                <ul>
                                    <li><b>Rotation</b> – The Earth spins on its axis.
                                        <ul>
                                            <li>Takes <b>24 hours</b> to complete one rotation.</li>
                                            <li>Causes <b>day and night</b>.</li>
                                        </ul>
                                    </li>
                                    <li><b>Revolution</b> – The Earth moves around the Sun in an orbit.
                                        <ul>
                                            <li>Takes <b>365 days</b> to complete one revolution.</li>
                                            <li>Causes <b>seasons</b>.</li>
                                        </ul>
                                    </li>
                                </ul>
                            </section>

                            <section>
                                <h2>Continents and Oceans</h2>
                                <p>The Earth&apos;s surface is made up of <b>land</b> and <b>water</b>.</p>
                                <div className="highlight">
                                    <b>Continents (7):</b> Asia, Africa, North America, South America, Antarctica, Europe, Australia.
                                </div>
                                <div className="highlight">
                                    <b>Oceans (5):</b> Pacific, Atlantic, Indian, Southern, Arctic.
                                </div>
                            </section>

                            <section>
                                <h2>Landforms on Earth</h2>
                                <ul>
                                    <li><b>Mountains</b> – High and steep areas.</li>
                                    <li><b>Hills</b> – Smaller than mountains.</li>
                                    <li><b>Plains</b> – Flat areas, good for farming.</li>
                                    <li><b>Plateaus</b> – Flat land at a high level.</li>
                                    <li><b>Deserts</b> – Dry areas with very little rainfall.</li>
                                </ul>
                            </section>

                            <section>
                                <h2>Water Bodies</h2>
                                <ul>
                                    <li><b>Rivers</b> – Flowing water that ends in seas or oceans.</li>
                                    <li><b>Lakes</b> – Water surrounded by land.</li>
                                    <li><b>Seas</b> – Smaller than oceans but still large water bodies.</li>
                                </ul>
                            </section>

                            <section>
                                <h2>Importance of the Earth</h2>
                                <ul>
                                    <li>Provides <b>air</b>, <b>water</b>, and <b>land</b> for all living things.</li>
                                    <li>Has the right temperature for life.</li>
                                    <li>Gives us food, shelter, and natural resources.</li>
                                </ul>
                            </section>

                            <section>
                                <h2>How to Take Care of the Earth</h2>
                                <ul>
                                    <li>Plant more trees.</li>
                                    <li>Reduce waste and recycle.</li>
                                    <li>Save water and electricity.</li>
                                    <li>Avoid pollution.</li>
                                </ul>
                            </section>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <header>
                            <h1>Class 3 Social Studies – The Earth: An Introduction</h1>
                        </header>

                        <section>
                            <h2>Introduction</h2>
                            <p>The Earth is our home. It is one of the eight planets in the Solar System and the only one known to have life. From mountains to oceans, forests to deserts, the Earth is full of amazing landforms and natural wonders.</p>
                        </section>

                        <section>
                            <h2>Shape of the Earth</h2>
                            <ul>
                                <li>Long ago, people thought the Earth was flat.</li>
                                <li>Now we know it is round, like a ball, but slightly flattened at the top and bottom. This shape is called a <b>sphere</b> or <b>geoid</b>.</li>
                                <li>We cannot feel the Earth moving, but it is always spinning and moving around the Sun.</li>
                            </ul>
                        </section>

                        <section>
                            <h2>Movements of the Earth</h2>
                            <p>The Earth has two main types of movements:</p>
                            <ul>
                                <li><b>Rotation</b> – The Earth spins on its axis.
                                    <ul>
                                        <li>Takes <b>24 hours</b> to complete one rotation.</li>
                                        <li>Causes <b>day and night</b>.</li>
                                    </ul>
                                </li>
                                <li><b>Revolution</b> – The Earth moves around the Sun in an orbit.
                                    <ul>
                                        <li>Takes <b>365 days</b> to complete one revolution.</li>
                                        <li>Causes <b>seasons</b>.</li>
                                    </ul>
                                </li>
                            </ul>
                        </section>

                        <section>
                            <h2>Continents and Oceans</h2>
                            <p>The Earth&apos;s surface is made up of <b>land</b> and <b>water</b>.</p>
                            <div className="highlight">
                                <b>Continents (7):</b> Asia, Africa, North America, South America, Antarctica, Europe, Australia.
                            </div>
                            <div className="highlight">
                                <b>Oceans (5):</b> Pacific, Atlantic, Indian, Southern, Arctic.
                            </div>
                        </section>

                        <section>
                            <h2>Landforms on Earth</h2>
                            <ul>
                                <li><b>Mountains</b> – High and steep areas.</li>
                                <li><b>Hills</b> – Smaller than mountains.</li>
                                <li><b>Plains</b> – Flat areas, good for farming.</li>
                                <li><b>Plateaus</b> – Flat land at a high level.</li>
                                <li><b>Deserts</b> – Dry areas with very little rainfall.</li>
                            </ul>
                        </section>

                        <section>
                            <h2>Water Bodies</h2>
                            <ul>
                                <li><b>Rivers</b> – Flowing water that ends in seas or oceans.</li>
                                <li><b>Lakes</b> – Water surrounded by land.</li>
                                <li><b>Seas</b> – Smaller than oceans but still large water bodies.</li>
                            </ul>
                        </section>

                        <section>
                            <h2>Importance of the Earth</h2>
                            <ul>
                                <li>Provides <b>air</b>, <b>water</b>, and <b>land</b> for all living things.</li>
                                <li>Has the right temperature for life.</li>
                                <li>Gives us food, shelter, and natural resources.</li>
                            </ul>
                        </section>

                        <section>
                            <h2>How to Take Care of the Earth</h2>
                            <ul>
                                <li>Plant more trees.</li>
                                <li>Reduce waste and recycle.</li>
                                <li>Save water and electricity.</li>
                                <li>Avoid pollution.</li>
                            </ul>
                        </section>

                    </article>
                    {currentQuestion ? (
                        <>
                            <h3 className="mb-3">{currentQuestion.question}</h3>
                            {(currentQuestion.type === "multiple-choice" ||
                                currentQuestion.type === "fill-in-the-blank") && (
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.answer.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                )}
                            {currentQuestion.type === "true-false" && (
                                <IonRadioGroup
                                    onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                >
                                    <IonList>
                                        {["true", "false"].map((option, index) => (
                                            <IonItem key={index} className={`${selectedOption === option
                                                ? option.toLowerCase() === String(currentQuestion.answer).toLowerCase()
                                                    ? "correct"
                                                    : "errorItems"
                                                : ""
                                                }`}>
                                                <IonRadio slot="start" value={option} />
                                                <IonLabel>{option.toUpperCase()}</IonLabel>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonRadioGroup>
                            )}

                            {(showExplanation) ? (
                                <>
                                    <span><strong>Correct option:</strong> {currentQuestion.answer}</span>
                                    <div dangerouslySetInnerHTML={{ __html: currentQuestion.explanation }}>
                                    </div>
                                    <div className="mt-3 text-center">
                                        <button className="btn hero-btn" onClick={loadRandomSyllabusQuestions}>Next Question</button>
                                    </div>
                                </>
                            ) : (<></>)}
                        </>
                    ) : (
                        <p>Loading question...</p>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default EarthIntroduction;
